import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, UserPlus } from "lucide-react";
import { toast } from "sonner";

const MarketingPanel = ({ user }) => {
  const navigate = useNavigate();
  const [websiteList, setWebsiteList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    marketing_name: "",
    website: "",
    username: "",
    kode_db: "",
    phone: "",
    member_type: "regis",
    nominal_depo: ""
  });

  useEffect(() => {
    fetchWebsiteList();
    
    // Set marketing name otomatis dari user yang login
    if (user && user.name) {
      setFormData(prev => ({ ...prev, marketing_name: user.name }));
    }
  }, [user]);

  const fetchWebsiteList = async () => {
    try {
      const response = await axiosInstance.get("/website-list");
      setWebsiteList(response.data);
    } catch (error) {
      toast.error("Gagal memuat data website");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        marketing_name: formData.marketing_name,
        website: formData.website,
        username: formData.username,
        kode_db: formData.kode_db,
        phone: "62" + formData.phone,
        member_type: formData.member_type,
        ...(formData.member_type === "ndp" ? { nominal_depo: parseFloat(formData.nominal_depo) } : {})
      };

      await axiosInstance.post("/members", payload);
      toast.success("Member berhasil ditambahkan");
      
      // Reset form (keep marketing_name)
      setFormData({
        marketing_name: user?.name || "",
        website: "",
        username: "",
        kode_db: "",
        phone: "",
        member_type: "regis",
        nominal_depo: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menambahkan member");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]" data-testid="marketing-panel">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-red-900/20"
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2" size={18} />
              Kembali
            </Button>
            <h1 className="text-2xl font-bold text-red-50">Input Member Baru</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Form Input */}
          <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
            <CardHeader>
              <CardTitle className="text-red-50 flex items-center gap-2">
                <UserPlus size={24} />
                Form Input Member
              </CardTitle>
              <CardDescription className="text-gray-400">Masukkan data member baru</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Nama Marketing</Label>
                  <div className="bg-[#0f0f10] border border-red-900/30 rounded-md px-3 py-2 text-white">
                    {formData.marketing_name || user?.name || '-'}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website" className="text-gray-300">Website *</Label>
                  <Select value={formData.website} onValueChange={(value) => setFormData({ ...formData, website: value })} required>
                    <SelectTrigger className="bg-[#0f0f10] border-red-900/30 text-white" data-testid="website-select">
                      <SelectValue placeholder="Pilih website" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1b] border-red-900/30 text-white">
                      {websiteList.map((item) => (
                        <SelectItem key={item.id} value={item.name} className="text-white hover:bg-red-900/20 focus:bg-red-900/30 focus:text-white">{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username" className="text-gray-300">Username *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="username-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="kode_db" className="text-gray-300">Kode DB *</Label>
                  <Input
                    id="kode_db"
                    value={formData.kode_db}
                    onChange={(e) => setFormData({ ...formData, kode_db: e.target.value })}
                    required
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="kode-db-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-300">Nomor Handphone *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white font-medium">62</span>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setFormData({ ...formData, phone: value });
                      }}
                      required
                      placeholder="8123456789"
                      className="bg-[#0f0f10] border-red-900/30 text-white pl-9"
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-gray-400">Hanya masukkan angka tanpa 62 atau 0 di depan</p>
                </div>

                <div className="space-y-3">
                  <Label className="text-gray-300">Status Member *</Label>
                  <RadioGroup
                    value={formData.member_type}
                    onValueChange={(value) => setFormData({ ...formData, member_type: value, nominal_depo: "" })}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="regis" id="regis" className="border-red-600 text-red-600" data-testid="radio-regis" />
                      <Label htmlFor="regis" className="text-white cursor-pointer">Regis</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="ndp" id="ndp" className="border-red-600 text-red-600" data-testid="radio-ndp" />
                      <Label htmlFor="ndp" className="text-white cursor-pointer">NDP</Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.member_type === "ndp" && (
                  <div className="space-y-2">
                    <Label htmlFor="nominal_depo" className="text-gray-300">Nominal Depo *</Label>
                    <Input
                      id="nominal_depo"
                      type="number"
                      step="0.01"
                      value={formData.nominal_depo}
                      onChange={(e) => setFormData({ ...formData, nominal_depo: e.target.value })}
                      required
                      className="bg-[#0f0f10] border-red-900/30 text-white"
                      data-testid="nominal-depo-input"
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-6"
                  disabled={loading}
                  data-testid="submit-member-button"
                >
                  {loading ? "Menyimpan..." : "Tambah Member"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default MarketingPanel;